import { z } from "zod";
import { microToUsd, usdToMicro, type Micro } from "./money.js";
import type { ActionKind } from "./review.js";

/**
 * The overnight preparer ([36](../../../docs/36-the-morning-list.md) §the preparer, ADR 0064 point 5): what Joogoos may
 * spend in a week preparing a business's posts and articles while nobody is pressing anything, and the numbers it
 * prepares to.
 *
 * It is the first thing in the product that spends a business's money with nobody pressing a button that day, so the
 * permission is one number the owner types, **zero by default**, and zero is off. Every step it takes is still a call
 * through `runCall` with every guard behind it — the person's caps, their credit, the box's ceiling — so the week's
 * number is one more bound and never the only one. It drafts and stops: what it makes waits on the morning list for
 * a yes, through each tool's own route.
 *
 * Letters are not in it. The Customers loop prepares those already, inside its own weekly number on its own page
 * (ADR 0039), and a second budget for the same letters would be two numbers that disagree.
 */

/**
 * Every call the preparer makes, and every job it asks for, carries a trace that starts with this. It is how its own
 * spending is told apart from a person's press of the same button — a topic plan asked for on SEO is `seo-…` — and so
 * what *spent this week* counts.
 */
export const PREPARE_TRACE = "prepare-";

/** The trace for one business on one night: the prefix, the end of its id, and the UTC day. */
export const prepareTrace = (workspaceId: string, now: Date): string =>
  `${PREPARE_TRACE}${workspaceId.slice(-8)}-${now.toISOString().slice(0, 10)}`;

/**
 * The thresholds, and why they are these. Three of a kind waiting is a morning's reading: fewer, and the list is thin
 * enough to be worth filling; more, and preparing another batch is spending on what the owner has not had time to
 * read. They are the owner's to change, here, in one place.
 */
export const PREPARE = {
  /** Fewer post drafts than this waiting, and the drafter runs once. */
  postsEnough: 3,
  /** Fewer topics than this proposed or kept and not written, and Plan topics is asked for. */
  topicsEnough: 3,
  /** Ideas asked of one batch: each is a post per network, so three ideas on two networks is six drafts. */
  ideas: 3,
} as const;

/** The most an owner may set a week to. A bound on a slip of the keyboard, not a recommendation. */
export const PREPARE_MAX_MICRO: Micro = 100_000_000n;

/** A week's amount as an owner types it: dollars, with cents or without, from nothing to `PREPARE_MAX_MICRO`. */
export const PrepareWeekly = z.string().trim().regex(/^\d{1,3}(\.\d{1,2})?$/, "say it in dollars, like 5 or 2.50")
  .transform((s) => usdToMicro(s))
  .refine((m) => m <= PREPARE_MAX_MICRO, `at most $${microToUsd(PREPARE_MAX_MICRO)} a week`);

/** What the Overview shows under the morning list (`GET /v1/waiting`). */
export interface Preparing {
  /** What the owner lets Joogoos spend a week preparing, in dollars. `"0.00"` is off. */
  budgetUsd: string;
  /** What the preparer's own calls have been charged this week (Monday to Sunday, UTC), and what they hold open. */
  spentUsd: string;
  /** Whether a marketing review is written each Monday, out of the same week's amount (35 phase 2). */
  reviewWeekly: boolean;
}

/** What kind of work a night prepares. Letters are the Customers loop's, and never here. */
export type PrepareKind = "posts" | "articles";

/**
 * Which kind a night prepares first: what the owner said yes to in the week's review (ADR 0064 point 5), so the daily
 * work follows the weekly decision instead of running beside it. A yes to a post or a week of posts puts posts
 * first; a yes to an article puts articles first. Anything else — letters, a fix, waiting — leaves the usual order,
 * posts then articles, because a post costs a cent and an article a good deal more, and a thin week's budget is better
 * spent on the cheap thing first.
 */
export function prepareOrder(decided: ActionKind | null | undefined): PrepareKind[] {
  return decided === "write_article" ? ["articles", "posts"] : ["posts", "articles"];
}

/** The line under the morning list, in the owner's words: what Joogoos may spend, and what it has. */
export function preparingSaid(p: Preparing): string {
  if (usdToMicro(p.budgetUsd) <= 0n) return "Joogoos prepares nothing overnight for this business.";
  return `Joogoos may spend up to $${p.budgetUsd} a week preparing posts and articles for you overnight, for you to say yes to here. $${p.spentUsd} spent this week.`;
}
